import { Optional, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';

import { viewService } from '@unpispas/upp-base';

interface ErrorInfo {
    [key: string]: string;
}

/**
 * Text field with an optional dropdown of string options (type or pick).
 * Implements `ControlValueAccessor` for reactive forms.
 */
@Component({
    selector: 'upp-input-select',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-input-select.html',
    styleUrls: [
        './upp-input-select.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppInputSelectComponent),
            multi: true
        }
    ]
})
export class UppInputSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
    /** Placeholder for the text field. */
    @Input() placeholder = '';
    /** Form control name when used inside a reactive `FormGroup`. */
    @Input() formControlName = '';
    /** Disables typing and picking. */
    @Input() disabled = false;
    /** Makes the text field read-only (options can still be picked unless disabled). */
    @Input() readonly = false;
    /** Floating caption above the field (same treatment as `upp-input` title). */
    @Input() title = '';
    /** Enables kiosk keyboard mode on the underlying text field. */
    @Input() kiosk = true;
    /** Option labels/values shown in the dropdown. Empty list hides the expand control. */
    @Input() options: string[] = [];
    /** Validation messages keyed by form error name. */
    @Input() errornfo: ErrorInfo = {};

    /** Emits when the bound value changes from typing or picking. */
    @Output() Changed = new EventEmitter<string | null>();
    /** Emits when the text field gains focus. */
    @Output() Focus = new EventEmitter<void>();

    formGroup: FormGroup | null = null;
    formControl: AbstractControl | null = null;

    private _innerValue: string | null | undefined = undefined;
    private _open = false;
    private _disabled = false;
    private _formSubscription: Subscription | null = null;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (_v: string | null) => {
        // required by Angular forms
    };
    private _onTouchCallback = () => {
        // required by Angular forms
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    get value(): string | null {
        if (this._innerValue === null || this._innerValue === undefined || this._innerValue === '') {
            return null;
        }
        return this._innerValue;
    }

    set value(v: string | null) {
        const _first = (this._innerValue === undefined);
        if (v !== this._innerValue) {
            this._innerValue = v;
            this._onChangeCallback(v);
            this._onTouchCallback();
            if (!_first) {
                this.Changed.emit(v);
            }
            this.change.markForCheck();
        }
    }

    get IsOpen(): boolean {
        return this._open && this.HasOptions && !this.IsDisabled;
    }

    get HasOptions(): boolean {
        return this.Options.length > 0;
    }

    /** Normalized non-empty option strings. */
    get Options(): string[] {
        const _out: string[] = [];
        for (const _item of this.options || []) {
            const _name = String(_item || '').trim();
            if (_name && !_out.includes(_name)) {
                _out.push(_name);
            }
        }
        return _out;
    }

    get IsDisabled(): boolean {
        return this._disabled || this.disabled || !!this.formControl?.disabled;
    }

    get showTitle(): boolean {
        return !!this.title && this.value !== null;
    }

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    constructor(
        @Optional() private controlContainer: ControlContainer,
        private change: ChangeDetectorRef,
        private hostRef: ElementRef<HTMLElement>,
        public view: viewService,
    ) {
        // nothing to do
    }

    ngOnInit(): void {
        this._innerValue = this.value;

        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective) {
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName)
            ? this.formGroup.get(this.formControlName)
            : null;

        if (this.formControl) {
            this.value = this._Normalize(this.formControl.value);
            this._formSubscription = this.formControl.valueChanges.subscribe(() => {
                this.value = this._Normalize(this.formControl?.value);
            });
        }
    }

    ngOnDestroy(): void {
        this._formSubscription?.unsubscribe();
        this._formSubscription = null;
    }

    writeValue(value: unknown): void {
        const _next = this._Normalize(value);
        if (_next !== this._innerValue) {
            this._innerValue = _next;
            this.change.markForCheck();
        }
    }

    registerOnChange(fn: (v: string | null) => void): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouchCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
        this.change.markForCheck();
    }

    OnFocus(): void {
        this.Focus.emit();
    }

    OnTextChanged(raw: string | null): void {
        if (this.IsDisabled || this.readonly) {
            return;
        }
        if (raw === null || raw === undefined || raw === '') {
            this.value = null;
            return;
        }
        this.value = String(raw);
    }

    OnToggle(ev?: Event): void {
        ev?.preventDefault();
        ev?.stopPropagation();
        if (this.IsDisabled || !this.HasOptions) {
            return;
        }
        this._open = !this._open;
        this._onTouchCallback();
        this.change.markForCheck();
    }

    OnPick(option: string): void {
        if (this.IsDisabled) {
            return;
        }
        this.value = this._Normalize(option);
        this._open = false;
        this.change.markForCheck();
    }

    @HostListener('document:click', ['$event'])
    private _onDocumentClick(ev: MouseEvent): void {
        if (!this._open) {
            return;
        }
        const host = this.hostRef.nativeElement;
        const path = typeof ev.composedPath === 'function' ? ev.composedPath() : [];
        if (path.includes(host)) {
            return;
        }
        const t = ev.target;
        if (t && host.contains(t as Node)) {
            return;
        }
        this._open = false;
        this.change.markForCheck();
    }

    @HostListener('document:keydown', ['$event'])
    private _onDocumentKeydown(ev: KeyboardEvent): void {
        if (ev.key !== 'Escape' || !this._open) {
            return;
        }
        ev.preventDefault();
        this._open = false;
        this.change.markForCheck();
    }

    private _Normalize(raw: unknown): string | null {
        if (raw === null || raw === undefined || raw === '') {
            return null;
        }
        const _value = String(raw).trim();
        return _value || null;
    }
}
